import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import UserBadge from './UserBadge';
import { useNavigate } from 'react-router-dom';
import TournamentStatsModal from '@/components/modals/TournamentStatsModal';

interface AppHeaderProps {
  onSignOut: () => void;
  title?: string;
  subtitle?: string;
  onNavigate?: () => void;
  navIcon?: 'home' | 'admin' | 'tournaments' | 'back';
  rightExtra?: React.ReactNode;
  tournamentId?: string;
  tournamentName?: string;
}

export default function AppHeader({
  onSignOut,
  title = 'THROWBALL SCORER',
  subtitle,
  onNavigate,
  navIcon,
  rightExtra,
  tournamentId,
  tournamentName,
}: AppHeaderProps) {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [showPointsTable, setShowPointsTable] = useState(false);

  const resolvedIcon = navIcon ?? (isAdmin ? 'admin' : 'home');
  const defaultNav = isAdmin ? () => navigate('/admin') : () => navigate('/scorer');
  const resolvedNav = onNavigate ?? defaultNav;

  const navLabel = resolvedIcon === 'admin'
    ? 'Admin Dashboard'
    : resolvedIcon === 'home'
    ? 'Match Dashboard'
    : resolvedIcon === 'tournaments'
    ? 'Tournaments'
    : 'Back';

  return (
    <>
      <header className="bg-surface border-b border-border px-4 py-3 safe-top flex items-center gap-2">
        {/* Left: user info */}
        <div className="w-[90px] sm:w-[140px] flex-shrink-0">
          <UserBadge />
        </div>

        {/* Center: title */}
        <div className="flex-1 text-center min-w-0 px-1">
          <h1 className="text-sm sm:text-base font-bold text-text-primary leading-tight truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="text-[10px] text-text-muted leading-tight truncate">{subtitle}</p>
          )}
        </div>

        {/* Right: extras + points table + nav + sign-out */}
        <div className="w-[90px] sm:w-[140px] flex-shrink-0 flex items-center justify-end gap-0.5">
          {rightExtra}

          {/* Points Table button - only when inside a tournament */}
          {tournamentId && (
            <button
              type="button"
              onClick={() => setShowPointsTable(true)}
              className="p-2 rounded-lg text-text-muted hover:text-warning hover:bg-surface-raised transition-colors"
              aria-label="Tournament Statistics"
              title="Tournament Statistics"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
              </svg>
            </button>
          )}

          {/* Nav icon */}
          <button
            type="button"
            onClick={resolvedNav}
            className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-raised transition-colors"
            aria-label={navLabel}
            title={navLabel}
          >
            {resolvedIcon === 'admin' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
                <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
              </svg>
            )}
            {resolvedIcon === 'home' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            )}
            {resolvedIcon === 'tournaments' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 4h14" /><path d="M5 4v6a7 7 0 0 0 14 0V4" />
                <line x1="12" y1="16" x2="12" y2="20" />
                <line x1="9" y1="20" x2="15" y2="20" />
                <path d="M9 20a4 4 0 0 0-4 4" />
                <path d="M15 20a4 4 0 0 1 4 4" />
              </svg>
            )}
            {resolvedIcon === 'back' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            )}
          </button>

          {/* Sign out */}
          <button
            type="button"
            onClick={onSignOut}
            className="p-2 rounded-lg text-text-muted hover:text-error hover:bg-surface-raised transition-colors"
            aria-label="Sign out"
            title="Sign Out"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16,17 21,12 16,7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </header>

      {/* Tournament Stats Modal - full-screen overlay */}
      {showPointsTable && tournamentId && (
        <TournamentStatsModal
          tournamentId={tournamentId}
          tournamentName={tournamentName}
          onClose={() => setShowPointsTable(false)}
        />
      )}
    </>
  );
}
